import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { LanguageWrapper } from "@/components/LanguageWrapper";
import '@/i18n/config';

// Eagerly load the main landing page for best LCP
import Index from "./pages/Index";

// Lazy load all other pages to reduce initial bundle size
const Pricing = lazy(() => import("./pages/Pricing"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const SiteSuccess = lazy(() => import("./pages/SiteSuccess"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const SitePreview = lazy(() => import("./pages/SitePreview"));
const SiteEditor = lazy(() => import("./pages/SiteEditor"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

// Language-prefixed routes component
const LocalizedRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Root redirect to language-prefixed home */}
      <Route path="/" element={<LanguageWrapper><Navigate to="/en/" replace /></LanguageWrapper>} />
      
      {/* English Routes */}
      <Route path="/en" element={<Navigate to="/en/" replace />} />
      <Route path="/en/" element={<Index />} />
      <Route path="/en/pricing" element={<Pricing />} />
      <Route path="/en/how-it-works" element={<HowItWorks />} />
      <Route path="/en/login" element={<Auth />} />
      <Route path="/en/register" element={<Auth />} />
      <Route path="/en/dashboard" element={<Dashboard />} />
      <Route path="/en/onboarding" element={<Onboarding />} />
      <Route path="/en/success" element={<SiteSuccess />} />
      <Route path="/en/editor/:siteId" element={<SiteEditor />} />
      <Route path="/en/admin" element={<AdminDashboard />} />
      <Route path="/en/terms" element={<Terms />} />
      <Route path="/en/privacy" element={<Privacy />} />
      
      {/* Spanish Routes */}
      <Route path="/es" element={<Navigate to="/es/" replace />} />
      <Route path="/es/" element={<Index />} />
      <Route path="/es/precios" element={<Pricing />} />
      <Route path="/es/como-funciona" element={<HowItWorks />} />
      <Route path="/es/iniciar-sesion" element={<Auth />} />
      <Route path="/es/registrarse" element={<Auth />} />
      <Route path="/es/panel" element={<Dashboard />} />
      <Route path="/es/incorporacion" element={<Onboarding />} />
      <Route path="/es/exito" element={<SiteSuccess />} />
      <Route path="/es/editor/:siteId" element={<SiteEditor />} />
      <Route path="/es/admin" element={<AdminDashboard />} />
      <Route path="/es/terminos" element={<Terms />} />
      <Route path="/es/privacidad" element={<Privacy />} />
      
      {/* Public site preview (language-neutral) */}
      <Route path="/s/:slug" element={<SitePreview />} />
      
      {/* Legacy routes - redirect to English versions */}
      <Route path="/pricing" element={<Navigate to="/en/pricing" replace />} />
      <Route path="/how-it-works" element={<Navigate to="/en/how-it-works" replace />} />
      <Route path="/login" element={<Navigate to="/en/login" replace />} />
      <Route path="/register" element={<Navigate to="/en/register" replace />} />
      <Route path="/dashboard" element={<Navigate to="/en/dashboard" replace />} />
      <Route path="/onboarding" element={<Navigate to="/en/onboarding" replace />} />
      <Route path="/success" element={<Navigate to="/en/success" replace />} />
      <Route path="/terms" element={<Navigate to="/en/terms" replace />} />
      <Route path="/privacy" element={<Navigate to="/en/privacy" replace />} />
      <Route path="/admin" element={<Navigate to="/en/admin" replace />} />
      <Route path="/editor/:siteId" element={<Navigate to="/en/editor/:siteId" replace />} />
      
      {/* Catch-all 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <LanguageProvider>
            <LocalizedRoutes />
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
